import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";

const JENIS = ["DP", "Cicilan", "Pelunasan"];
const METODE = ["Cash", "Transfer", "Giro", "QRIS"];
const STATUS_CLS = { "Belum Bayar": "bg-slate-100 text-slate-700", "DP": "bg-blue-100 text-blue-800", "Cicilan": "bg-amber-100 text-amber-800", "Lunas": "bg-emerald-100 text-emerald-800", "Overpaid": "bg-purple-100 text-purple-800" };
const SCHED_CLS = { "Sudah Dibayar": "bg-emerald-100 text-emerald-800", "Terlambat": "bg-rose-100 text-rose-800", "Jatuh Tempo": "bg-amber-100 text-amber-800", "Belum Jatuh Tempo": "bg-slate-100 text-slate-700" };

// Dialog pembayaran DP/cicilan + jadwal cicilan — dipakai halaman Penjualan & Piutang
export function PaymentDialog({ invoice, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [editPay, setEditPay] = useState(null);
  const [form, setForm] = useState({ amount: "", jenis: "auto", metode: "Cash", ref_no: "", note: "", pay_date: "" });
  const [schedAuto, setSchedAuto] = useState({ count: "3", first_due: "" });
  const [schedRow, setSchedRow] = useState({ due_date: "", amount: "" });

  const load = useCallback(async () => {
    if (!invoice) return;
    try {
      const r = await api.get(`/invoices/${invoice.invoice_id}`);
      setDetail(r.data);
      setEditPay(null);
      setForm({ amount: String(Math.max(r.data.total - (r.data.paid_amount || 0), 0)), jenis: "auto", metode: "Cash", ref_no: "", note: "", pay_date: new Date().toISOString().slice(0, 10) });
    } catch { toast.error("Gagal memuat invoice"); }
  }, [invoice]);
  useEffect(() => { setDetail(null); setEditPay(null); load(); }, [load]);

  if (!invoice) return null;
  const paid = detail?.paid_amount || 0;
  const remaining = detail ? detail.total - paid : 0;
  const status = detail?.payment_status || "Belum Bayar";

  const submit = async () => {
    try {
      const body = { amount: Number(form.amount), jenis: form.jenis === "auto" ? undefined : form.jenis, metode: form.metode, ref_no: form.ref_no, note: form.note, pay_date: form.pay_date };
      const r = editPay
        ? await api.put(`/invoices/${invoice.invoice_id}/payments/${editPay.payment_id}`, body)
        : await api.post(`/invoices/${invoice.invoice_id}/payments`, body);
      toast.success(`${r.data.payment_status} — sisa ${rupiah(r.data.remaining)}`);
      load(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mencatat pembayaran"); }
  };

  const delPay = async (p) => {
    if (!window.confirm(`Hapus pembayaran ${rupiah(p.amount)}? Sisa piutang akan dihitung ulang.`)) return;
    try {
      const r = await api.delete(`/invoices/${invoice.invoice_id}/payments/${p.payment_id}`);
      toast.success(`Pembayaran dihapus — sisa ${rupiah(r.data.remaining)}`);
      load(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const startEdit = (p) => {
    setEditPay(p);
    setForm({ amount: String(p.amount), jenis: p.jenis || "Cicilan", metode: p.metode || "Cash", ref_no: p.ref_no || "", note: p.note || "", pay_date: p.pay_date || "" });
  };

  const saveSchedule = async (items) => {
    try {
      await api.put(`/invoices/${invoice.invoice_id}/schedule`, { items });
      toast.success("Jadwal cicilan tersimpan");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan jadwal"); }
  };

  const addSchedRow = () => {
    if (!schedRow.due_date || Number(schedRow.amount) <= 0) return toast.error("Isi tanggal & nominal jadwal");
    const items = (detail.payment_schedule || []).map(s => ({ schedule_id: s.schedule_id, due_date: s.due_date, amount: s.amount }));
    items.push({ due_date: schedRow.due_date, amount: Number(schedRow.amount) });
    setSchedRow({ due_date: "", amount: "" });
    saveSchedule(items);
  };

  const delSchedRow = (sid) => saveSchedule((detail.payment_schedule || []).filter(s => s.schedule_id !== sid));

  const autoGen = async () => {
    if (!schedAuto.first_due) return toast.error("Isi tanggal cicilan pertama");
    try {
      await api.post(`/invoices/${invoice.invoice_id}/schedule/auto`, { count: Number(schedAuto.count), first_due: schedAuto.first_due });
      toast.success("Jadwal cicilan otomatis dibuat");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat jadwal"); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Pembayaran — {invoice.trx_no}</DialogTitle>
          <DialogDescription>DP, cicilan, pelunasan & jadwal pembayaran invoice.</DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="rounded border p-2"><div className="text-[10px] font-mono uppercase text-slate-500">Total</div><div className="font-mono font-bold" data-testid="pay-total">{rupiah(detail.total)}</div></div>
              <div className="rounded border p-2"><div className="text-[10px] font-mono uppercase text-slate-500">Terbayar</div><div className="font-mono font-bold text-emerald-700" data-testid="pay-paid">{rupiah(paid)}</div></div>
              <div className="rounded border p-2"><div className="text-[10px] font-mono uppercase text-slate-500">Sisa</div><div className="font-mono font-bold text-rose-700" data-testid="pay-remaining">{rupiah(remaining)}</div></div>
              <div className="rounded border p-2 grid place-items-center"><Badge className={`${STATUS_CLS[status]} font-normal`} data-testid="pay-status">{status}</Badge></div>
            </div>

            {(detail.payments || []).length > 0 && (
              <div>
                <div className="text-xs font-mono uppercase text-slate-500 mb-1">Riwayat Pembayaran</div>
                <table className="w-full text-xs" data-testid="payment-history">
                  <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="py-1 px-2">Tanggal</th><th>Jenis</th><th>Metode</th><th>Ref</th><th className="text-right">Nominal</th><th></th></tr></thead>
                  <tbody>
                    {detail.payments.map((p) => (
                      <tr key={p.payment_id} className="border-b">
                        <td className="py-1 px-2 font-mono">{formatDate(p.pay_date || p.created_at)}</td>
                        <td>{p.jenis || "Cicilan"}</td>
                        <td>{p.metode || "-"}</td>
                        <td className="font-mono text-slate-400">{p.ref_no || "-"}</td>
                        <td className="text-right font-mono">{rupiah(p.amount)}</td>
                        <td className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => startEdit(p)} data-testid={`edit-pay-${p.payment_id}`}><Pencil className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => delPay(p)} data-testid={`del-pay-${p.payment_id}`}><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {status !== "Lunas" && (
              <div className="rounded border p-3 space-y-2">
                <div className="text-xs font-mono uppercase text-slate-500">{editPay ? "Edit Pembayaran" : "Catat Pembayaran"}</div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-xs">Tanggal</Label><Input type="date" value={form.pay_date} onChange={(e) => setForm({ ...form, pay_date: e.target.value })} data-testid="input-pay-date" /></div>
                  <div><Label className="text-xs">Jenis</Label>
                    <Select value={form.jenis} onValueChange={(v) => setForm({ ...form, jenis: v })}>
                      <SelectTrigger data-testid="select-pay-jenis"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="auto">Otomatis</SelectItem>{JENIS.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Metode</Label>
                    <Select value={form.metode} onValueChange={(v) => setForm({ ...form, metode: v })}>
                      <SelectTrigger data-testid="select-pay-metode"><SelectValue /></SelectTrigger>
                      <SelectContent>{METODE.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Nominal (Rp)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="input-pay-amount" /></div>
                  <div><Label className="text-xs">No. Referensi</Label><Input value={form.ref_no} onChange={(e) => setForm({ ...form, ref_no: e.target.value })} placeholder="opsional" data-testid="input-pay-ref" /></div>
                  <div><Label className="text-xs">Catatan</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="opsional" /></div>
                </div>
                <div className="flex gap-2 justify-end">
                  {editPay && <Button variant="ghost" size="sm" onClick={() => { setEditPay(null); load(); }}>Batal Edit</Button>}
                  <Button variant="outline" size="sm" onClick={() => setForm({ ...form, amount: String(Math.max(remaining, 0)) })} data-testid="btn-fill-remaining">Isi Sisa Penuh</Button>
                  <Button size="sm" onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-payment">{editPay ? "Simpan Perubahan" : "Catat Pembayaran"}</Button>
                </div>
              </div>
            )}

            <div className="rounded border p-3 space-y-2">
              <div className="text-xs font-mono uppercase text-slate-500">Jadwal Cicilan</div>
              {(detail.schedule || []).length > 0 && (
                <table className="w-full text-xs" data-testid="schedule-table">
                  <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="py-1 px-2">Jatuh Tempo</th><th className="text-right">Nominal</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {detail.schedule.map((s) => (
                      <tr key={s.schedule_id} className="border-b">
                        <td className="py-1 px-2 font-mono">{formatDate(s.due_date)}</td>
                        <td className="text-right font-mono">{rupiah(s.amount)}</td>
                        <td><Badge className={`${SCHED_CLS[s.status]} font-normal`}>{s.status}</Badge></td>
                        <td className="text-right"><Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => delSchedRow(s.schedule_id)} data-testid={`del-sched-${s.schedule_id}`}><Trash2 className="h-3 w-3 text-rose-600" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <div><Label className="text-xs">Jatuh Tempo</Label><Input type="date" value={schedRow.due_date} onChange={(e) => setSchedRow({ ...schedRow, due_date: e.target.value })} data-testid="input-sched-date" /></div>
                <div><Label className="text-xs">Nominal</Label><Input type="number" value={schedRow.amount} onChange={(e) => setSchedRow({ ...schedRow, amount: e.target.value })} data-testid="input-sched-amount" /></div>
                <Button variant="outline" size="sm" onClick={addSchedRow} data-testid="btn-add-schedule"><Plus className="h-3.5 w-3.5 mr-1" />Tambah</Button>
              </div>
              <div className="flex items-end gap-2 border-t pt-2">
                <div><Label className="text-xs">Jumlah Cicilan</Label><Input type="number" className="w-24" value={schedAuto.count} onChange={(e) => setSchedAuto({ ...schedAuto, count: e.target.value })} data-testid="input-sched-count" /></div>
                <div><Label className="text-xs">Mulai</Label><Input type="date" value={schedAuto.first_due} onChange={(e) => setSchedAuto({ ...schedAuto, first_due: e.target.value })} data-testid="input-sched-first" /></div>
                <Button variant="outline" size="sm" onClick={autoGen} data-testid="btn-auto-schedule"><Sparkles className="h-3.5 w-3.5 mr-1" />Buat Otomatis</Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
